<template>
    <div class="global_content" :class="{ dark }">
        <div
            v-if="previewBanner"
            class="portal-preview-banner"
        >
            <div class="portal-preview-banner__copy">
                <strong>{{ t.previewAs.banner }}:</strong>
                <span>{{ previewBanner }}</span>
            </div>
            <button type="button" class="portal-preview-banner__exit" @click="exitPreview">
                {{ t.previewAs.exit }}
            </button>
        </div>

        <div class="courses-layout">
            <div class="header-courses-row">
                <div class="title">
                    <p>{{ $t('student.dashboard.myCourses') }}</p>
                </div>
                <div class="search-container">
                    <div class="search-input-wrapper">
                        <input
                            type="text"
                            v-model="search"
                            :placeholder="$t('student.dashboard.searchPlaceholder')"
                            class="search-input"
                        />
                        <span class="search-icon">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="2"/>
                                <line x1="14.2929" y1="14.7071" x2="18" y2="18.4142" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </span>
                    </div>
                </div>
            </div>

            <template v-if="hasReady && user && user.type != null">
                <section
                    v-for="group in filteredGroups"
                    :key="group.id"
                    class="course-group-section"
                >
                    <div class="course-group-section__head">
                        <div>
                            <div class="course-group-section__title-row">
                                <h2>{{ group.name }}</h2>
                                <span
                                    class="course-group-section__chip"
                                    :class="group.mode === 'path' ? 'is-path' : 'is-group'"
                                >
                                    {{ group.mode === 'path' ? t.groups.modePath : t.groups.modeGroup }}
                                </span>
                            </div>
                            <p class="course-group-section__lead">
                                {{
                                    group.mode === 'path'
                                        ? t.groups.sectionLeadPath
                                        : t.groups.sectionLeadGroup
                                }}
                            </p>
                        </div>
                    </div>
                    <div class="courses courses--group-slide">
                        <CourseCard
                            v-for="entry in group.entries"
                            :key="`${group.id}-${entry.key}`"
                            :image="entry.image"
                            :progress="entry.progress"
                            :title="entry.title"
                            :id="entry.openId"
                            :status="!entry.locked"
                            :data="entry.data"
                            :invertOrder="true"
                            :locked="entry.locked"
                            :lockReason="entry.lockReason"
                        />
                    </div>
                </section>

                <section
                    v-if="filteredOtherCourses.length || (!filteredGroups.length && filteredCourses.length)"
                    class="course-group-section course-group-section--others"
                >
                    <div
                        v-if="filteredGroups.length"
                        class="course-group-section__head"
                    >
                        <div>
                            <h2>{{ t.groups.othersTitle }}</h2>
                            <p class="course-group-section__lead">{{ t.groups.othersLead }}</p>
                        </div>
                    </div>
                    <div class="courses" v-show="displayFlatCourses.length">
                        <CourseCard
                            v-for="course in displayFlatCourses"
                            :key="course.course ? course.course.id : course.id"
                            :image="getCourseImage(course)"
                            :progress="course.course && course.course.progressPercentage != null ? (course.course.progressPercentage) : 0"
                            :title="course.course && course.course.name"
                            :id="user && user.type == 3 ? course.id : (course.course ? course.course.id : course.id)"
                            :status="course.course && course.course.status"
                            :data="course"
                            :invertOrder="true"
                        />
                    </div>
                    <div
                        v-if="!filteredGroups.length && displayFlatCourses.length === 0"
                        class="no-results"
                    >
                        {{ $t('student.courses.empty') }}
                    </div>
                </section>

                <div
                    v-if="filteredGroups.length && !filteredOtherCourses.length && !hasAnyGroupEntry"
                    class="no-results"
                >
                    {{ $t('student.courses.empty') }}
                </div>

                <div
                    v-if="isEmptyAccount"
                    class="no-results no-results--account"
                    role="status"
                >
                    {{ $t('student.courses.noneReleased') }}
                </div>
                <div
                    v-else-if="isEmptySearch"
                    class="no-results"
                    role="status"
                >
                    {{ $t('student.courses.empty') }}
                </div>
            </template>

            <span class="loader home" v-show="!hasReady || !user || user.type == null"></span>
        </div>
    </div>
</template>

<script>
import CourseCard from './CourseCard.vue';
import { mapActions, mapState } from 'vuex';
import { isCorporateMockEnabled } from '@/utils/corporate/environment';
import { getPortalViewerContext } from '@/utils/corporate/portalViewer';
import { corporateI18n } from '@/i18n/mixins';
import { courseCoverSrc } from '@/utils/courses';

export default {
    mixins: [corporateI18n],
    data() {
        return {
            search: '',
            courseGroups: [],
            searchDebounceTimeout: null,
        };
    },
    emits: ['search'],
    components: {
        CourseCard,
    },
    computed: {
        ...mapState(['dark', 'user']),
        showCorporateGroups() {
            return (this.courseGroups || []).length > 0;
        },
        previewBanner() {
            const ctx = getPortalViewerContext();
            return ctx.isPreview ? ctx.label : '';
        },
        filteredCourses() {
            if (!this.data || !this.user || this.user.type == null) return [];
            if (this.serverFiltered || !this.search) return this.data;
            const searchLower = this.search.toLowerCase();
            return this.data.filter((course) => {
                const name = course.course && course.course.name ? course.course.name.toLowerCase() : '';
                return name.includes(searchLower);
            });
        },
        groupedCourseIds() {
            const ids = new Set();
            (this.courseGroups || []).forEach((group) => {
                (group.entries || []).forEach((entry) => {
                    if (entry.matchedEnrolmentId != null) {
                        ids.add(String(entry.matchedEnrolmentId));
                    }
                    if (entry.matchedCourseId != null) {
                        ids.add(String(entry.matchedCourseId));
                    }
                });
            });
            return ids;
        },
        filteredOtherCourses() {
            if (!this.showCorporateGroups || !this.courseGroups.length) {
                return [];
            }
            return this.filteredCourses.filter((course) => {
                const enrolmentId = course?.id != null ? String(course.id) : null;
                const courseId = course?.course?.id != null ? String(course.course.id) : null;
                if (enrolmentId && this.groupedCourseIds.has(enrolmentId)) return false;
                if (courseId && this.groupedCourseIds.has(courseId)) return false;
                return true;
            });
        },
        displayFlatCourses() {
            if (this.showCorporateGroups && this.courseGroups.length) {
                return this.filteredOtherCourses;
            }
            return this.filteredCourses;
        },
        filteredGroups() {
            if (!this.showCorporateGroups) return [];
            if (this.serverFiltered) {
                return this.courseGroups || [];
            }
            const term = this.search.trim().toLowerCase();
            return (this.courseGroups || [])
                .map((group) => {
                    const entries = (group.entries || []).filter((entry) => {
                        if (!term) return true;
                        return String(entry.title || '').toLowerCase().includes(term);
                    });
                    return { ...group, entries };
                })
                .filter((group) => group.entries.length > 0);
        },
        hasAnyGroupEntry() {
            return this.filteredGroups.some((g) => g.entries.length > 0);
        },
        hasCatalogCourses() {
            const courses = Array.isArray(this.data) ? this.data.length : 0;
            return courses > 0 || this.hasAnyGroupEntry;
        },
        isEmptyAccount() {
            return this.hasCatalogReady && !this.hasCatalogCourses && !String(this.search || '').trim();
        },
        isEmptySearch() {
            return this.hasCatalogReady && !this.hasCatalogCourses && Boolean(String(this.search || '').trim());
        },
        hasCatalogReady() {
            return Boolean(this.hasReady && this.user && this.user.type != null);
        },
    },
    watch: {
        data: {
            deep: true,
            handler() {
                this.applyCourseGroups();
            },
        },
        groupCourses: {
            deep: true,
            handler() {
                this.applyCourseGroups();
            },
        },
        search() {
            if (!this.serverFiltered) {
                return;
            }
            clearTimeout(this.searchDebounceTimeout);
            this.searchDebounceTimeout = setTimeout(() => {
                this.$emit('search', this.search);
            }, 300);
        },
    },
    props: {
        view: Boolean,
        data: Array,
        groupCourses: {
            type: Array,
            default: () => [],
        },
        hasReady: Boolean,
        serverFiltered: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        this.applyCourseGroups();
    },
    methods: {
        ...mapActions(['clearPreviewAs']),
        exitPreview() {
            this.clearPreviewAs();
            this.$router.push('/dashboard/corporativo/visao-geral');
        },
        getCourseImage(course) {
            if (!course) return null;
            const nested = course.course && typeof course.course === 'object'
                ? course.course
                : null;
            return courseCoverSrc(nested, '') || courseCoverSrc(course, '') || null;
        },
        markUsedEnrolment(usedIds, enrolled) {
            if (!enrolled || !usedIds) return;
            if (enrolled.id != null) usedIds.add(String(enrolled.id));
            if (enrolled.course?.id != null) usedIds.add(String(enrolled.course.id));
        },
        isEnrolmentUsed(usedIds, course) {
            if (!usedIds || !course) return false;
            const enrolmentId = course?.id != null ? String(course.id) : null;
            const courseId = course?.course?.id != null ? String(course.course.id) : null;
            if (enrolmentId && usedIds.has(enrolmentId)) return true;
            if (courseId && usedIds.has(courseId)) return true;
            return false;
        },
        findEnrolledCourse(item, usedIds = null) {
            const list = Array.isArray(this.data) ? this.data : [];
            const byId = list.find((course) => {
                if (this.isEnrolmentUsed(usedIds, course)) return false;
                const courseId = course?.course?.id ?? course?.id;
                return courseId != null && String(courseId) === String(item.courseId);
            });
            if (byId) return { enrolled: byId, exactMatch: true };

            const name = String(item.courseName || '').trim().toLowerCase();
            if (name) {
                const byName = list.find((course) => {
                    if (this.isEnrolmentUsed(usedIds, course)) return false;
                    const courseName = String(course?.course?.name || '').trim().toLowerCase();
                    return courseName && courseName === name;
                });
                if (byName) return { enrolled: byName, exactMatch: true };
            }

            // Mock: associa cursos reais da matrícula aos itens do grupo para demo visual.
            if (isCorporateMockEnabled()) {
                const demo = list.find((course) => !this.isEnrolmentUsed(usedIds, course));
                if (demo) return { enrolled: demo, exactMatch: false };
            }

            return { enrolled: null, exactMatch: false };
        },
        buildGroupEntries(group, usedIds = new Set()) {
            return (group.items || []).map((item, index) => {
                const { enrolled, exactMatch } = this.findEnrolledCourse(item, usedIds);
                this.markUsedEnrolment(usedIds, enrolled);

                const progress = exactMatch && enrolled?.course?.progressPercentage != null
                    ? Number(enrolled.course.progressPercentage)
                    : (item.progress || 0);
                const openId = enrolled
                    ? (this.user?.type == 3 ? enrolled.id : (enrolled.course?.id ?? enrolled.id))
                    : null;
                const data = enrolled || {
                    id: item.courseId,
                    progress,
                    course: {
                        id: item.courseId,
                        name: item.courseName,
                        progressPercentage: progress,
                        status: true,
                        shortDescription: item.locked
                            ? (item.lockReason || '')
                            : (group.mode === 'path'
                                ? this.t.groups.sectionLeadPath
                                : this.t.groups.sectionLeadGroup),
                        moduleQty: null,
                        lessonQty: null,
                        imageFinalPath: item.imageFinalPath || null,
                        imageFinalPath2: item.imageFinalPath2 || null,
                    },
                };

                return {
                    key: `${item.courseId}-${index}`,
                    title: item.courseName || enrolled?.course?.name || this.$t('student.courseCard.courseFallback', { id: item.courseId }),
                    image: this.getCourseImage(enrolled) || this.getCourseImage(item) || null,
                    progress,
                    openId,
                    data,
                    locked: Boolean(item.locked),
                    lockReason: item.locked
                        ? (item.lockReason || this.t.groups.locked)
                        : '',
                    matchedEnrolmentId: enrolled?.id ?? null,
                    matchedCourseId: enrolled?.course?.id ?? null,
                };
            });
        },
        applyCourseGroups() {
            const groups = Array.isArray(this.groupCourses) ? this.groupCourses : [];
            const usedIds = new Set();
            this.courseGroups = groups.map((group) => ({
                ...group,
                entries: this.buildGroupEntries(group, usedIds),
            }));
        },
    },
};
</script>
<style>
.global_content {
    background-color: transparent;
    padding: 32px 0 8px;
}
.global_content.dark {
    background-color: transparent;
}

.portal-preview-banner {
    max-width: 1600px;
    margin: 0 24px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: rgba(37, 99, 235, 0.12);
    color: #1e3a8a;
    font-size: 0.9rem;
    box-sizing: border-box;
    min-width: 0;
    flex-wrap: wrap;
}

.portal-preview-banner__copy {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35em;
    min-width: 0;
    text-align: left;
}

.portal-preview-banner__exit {
    border: 0;
    border-radius: 10px;
    padding: 0.45rem 0.8rem;
    background: #1d4ed8;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
    flex-shrink: 0;
}

.global_content.dark .portal-preview-banner {
    background: rgba(26, 199, 208, 0.14);
    color: #d7f6f8;
}

.global_content.dark .portal-preview-banner__exit {
    background: #1ac7d0;
    color: #0f0e12;
}

.course-group-section {
    margin-bottom: 1.75rem;
}

.course-group-section__head {
    margin-bottom: 0.9rem;
}

.course-group-section__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.55rem;
}

.course-group-section__title-row h2 {
    margin: 0;
    color: var(--dash-text, #242424);
    font-family: Inter, system-ui, sans-serif;
    font-size: 1.15rem;
    font-weight: 700;
}

.global_content.dark .course-group-section__title-row h2 {
    color: var(--dash-text, #f3f4f6);
}

.course-group-section__chip {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
}

.course-group-section__chip.is-path {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.course-group-section__chip.is-group {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.course-group-section__lead {
    margin: 0.35rem 0 0;
    color: var(--dash-text-muted, #5c6370);
    font-size: 0.875rem;
}

.global_content .courses {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
    align-items: stretch;
}
.global_content .course {
    height: auto;
    min-height: 300px;
    max-width: none;
    background-repeat: no-repeat;
    position: relative;
    border-radius: 12px !important;
    cursor: pointer;
    background-size: cover;
    box-sizing: border-box;
    border: 1px solid var(--dash-border, rgba(15, 23, 42, 0.06));
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.global_content .course:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.global_content.dark .course:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
}

@media (max-width: 1024px) {
    .global_content .courses-layout {
        padding: 24px 20px;
    }
    .portal-preview-banner {
        margin-left: 20px;
        margin-right: 20px;
    }
    .global_content .courses {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 20px;
    }
    .global_content .course {
        min-height: 280px;
    }
}

@media (max-width: 959px) {
    .global_content {
        padding: 12px 0 8px;
    }

    .global_content .courses-layout {
        padding: 8px 10px 12px;
        max-width: none;
    }

    .global_content .courses {
        grid-template-columns: 1fr;
        gap: 14px;
    }

    .global_content .course {
        min-height: auto;
    }

    /* Slide horizontal só para grupos de cursos no mobile */
    .global_content .courses.courses--group-slide {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 12px;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        margin: 0 -10px;
        padding: 0 10px 8px;
    }

    .global_content .courses.courses--group-slide::-webkit-scrollbar {
        display: none;
    }

    .global_content .courses.courses--group-slide > .course {
        flex: 0 0 min(78vw, 300px);
        width: min(78vw, 300px);
        max-width: min(78vw, 300px);
        scroll-snap-align: start;
        scroll-snap-stop: always;
    }

    .global_content .header-courses-row {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        margin-bottom: 16px;
    }

    .global_content .title {
        width: 100%;
        text-align: left;
    }

    .global_content .title p {
        font-size: 1.125rem;
        text-align: left;
        color: #1F2937;
    }

    .global_content.dark .title p {
        color: #f2f4f8;
    }

    .global_content .search-container {
        width: 100% !important;
        max-width: 100% !important;
        margin-top: 0;
        justify-content: stretch;
        display: flex;
    }

    .global_content .search-input-wrapper {
        width: 100% !important;
        max-width: 100% !important;
    }

    .global_content .search-input {
        font-size: 16px;
        padding: 12px 40px 12px 14px;
        min-height: 44px;
        border-radius: 10px;
        border-color: #E4E7EC;
        width: 100%;
        box-sizing: border-box;
    }

    .course-group-section {
        margin-bottom: 1.25rem;
    }

    .course-group-section__title-row h2 {
        font-size: 1rem;
    }

    .course-group-section__lead {
        font-size: 0.8125rem;
    }

    .portal-preview-banner {
        flex-direction: column;
        align-items: flex-start;
        margin-left: 10px;
        margin-right: 10px;
    }
}

@media (max-width: 480px) {
    .global_content {
        padding: 8px 0 4px;
    }

    .global_content .title p {
        font-size: 1.0625rem;
        letter-spacing: -0.03em;
    }

    .global_content .no-results {
        font-size: 15px;
        margin-top: 20px;
    }

    .global_content .header-courses-row {
        margin-bottom: 12px;
    }
}

.global_content .title{
    margin: 0;
}
.global_content .courses-layout {
    max-width: 1600px;
    padding: 0 24px;
    margin: 0 auto;
    box-sizing: border-box;
}
.global_content .title p {
    color: var(--dash-text, #242424);
    font-family: Inter, system-ui, sans-serif;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.02em;
}
.global_content .infos {
    position: absolute;
    width: 100%;
    bottom: 5%;
    text-align: left;
}
.global_content .progress_course {
    align-items: center;
    display: flex;
    padding: 0 10px;
    color: #b4b1ad;
    font-size: 12px;
}
.global_content .infos p {
    padding: 0 25px;
    overflow: hidden;
    color: #F3F3F3;
    text-overflow: ellipsis;
    font-family: Inter;
    font-size: 17px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.4px;
    margin: 0;
}
.global_content .progress_course {
    display: flex;
}
.global_content.dark .title p {
    color: var(--dash-text, #f3f4f6);
}
.global_content .loader.home {
    z-index: 999999;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 48px;
    height: 48px;
    border: 5px solid #1AC7D0;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
}
.global_content .header-courses-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 20px;
}

.global_content .search-container {
    margin: 0;
    display: flex;
    justify-content: flex-end;
    width: 50%;
    max-width: 50%;
}
.global_content .search-input-wrapper {
    position: relative;
    width: 65%;
    display: flex;
    align-items: center;
}
.global_content .search-input {
    padding: 10px 42px 10px 16px;
    border: 1px solid var(--dash-border, #e5e7eb);
    border-radius: 10px;
    font-size: 15px;
    width: 100%;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    box-sizing: border-box;
    background: var(--dash-bg-elevated, #fff);
    color: var(--dash-text, #111827);
}
.global_content.dark .search-input {
    background: var(--dash-bg-elevated, #1a191f);
    color: var(--dash-text, #f3f4f6);
    border-color: var(--dash-border, rgba(255, 255, 255, 0.12));
}
.global_content .search-input::placeholder {
    color: var(--dash-text-muted, #9ca3af);
}
.global_content .search-input:focus {
    border-color: #1ac7d0;
    box-shadow: 0 0 0 3px rgba(26, 199, 208, 0.2);
}
.global_content .search-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    pointer-events: none;
    color: var(--dash-text-muted, #6b7280);
}
.global_content .no-results {
    width: 100%;
    text-align: center;
    color: var(--dash-text-muted, #888);
    font-size: 16px;
    margin-top: 40px;
}
.global_content .no-results--account {
    margin-top: 48px;
    padding: 28px 20px;
    border-radius: 16px;
    background: var(--dash-bg-elevated, #fff);
    color: var(--dash-text, #374151);
    font-size: 16px;
    font-weight: 500;
    line-height: 1.45;
}
.global_content.dark .no-results--account {
    background: var(--dash-bg-elevated, #1f2430);
    color: var(--dash-text, #e5e7eb);
}
</style>
